import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, Users, Search, Loader2, AlertTriangle,
  X, Check,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  searchWorkers, getCompanyWorkers, getEventsByRange, getWorkHours,
  DAYS_OF_WEEK, dayOfWeekMeta,
  EVENT_TYPES,
} from '../services/api';
import { filterWorkersForUser } from '../lib/permissions';
import { useMultiHolidays } from '../hooks/useHolidays';
import { describeHolidays } from '../lib/spainHolidays';
import { useLazyHourBalances, fetchEnrichedScheduleHistory } from '../hooks/useWorkerHourBalance';
import { buildEnrichedScheduleHistory, makeScheduleResolver, isRestDayAt } from '../lib/scheduleHistory';
import {
  eligibleForBalance,
  formatBalance, balanceColor,
  NO_SCHEDULE_SHORT_LABEL, NO_SCHEDULE_ADMIN_HINT, formatNoScheduleBalance,
} from '../lib/hourBalance';
import {
  buildDayStatusSets, resolveDayStatus, DAY_STATUS, DAY_STATUS_LABELS,
} from '../lib/dayStatus';
import VacationEditModal from '../components/VacationEditModal';
import { timeZoneForWorker, dayKeyInZone, todayInZone, addDaysToDayKey } from '../lib/workerTime';

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const DOW = ['L','M','X','J','V','S','D'];

const isoLocal = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const buildDaysOfMonth = (year, month) => {
  const lastDay = new Date(year, month + 1, 0).getDate();
  return Array.from({ length: lastDay }, (_, i) => {
    const date = new Date(year, month, i + 1);
    return { day: i + 1, iso: isoLocal(date), dow: (date.getDay() + 6) % 7 };
  });
};

// El ESTADO de cada día lo decide resolveDayStatus (src/lib/dayStatus.js, compartido con la app),
// la misma función que usa el calendario personal: misma prioridad y misma
// semántica de aprobación (pendientes visibles, fichajes pendientes aparte).
// Aquí solo mapeamos estado → estilo de la celda (td) y del recuadro
// interior (para el borde discontinuo de los pendientes).
const CELL_STYLE = {
  [DAY_STATUS.VACATION_APPROVED]: { td: 'bg-accent/60' },
  [DAY_STATUS.VACATION_PENDING]:  { td: 'bg-accent/15', box: 'rounded border border-dashed border-accent' },
  // Día completo: rojo full + texto blanco para que se distinga claramente de
  // vacaciones (rosa pastel) y comunique gravedad de un vistazo.
  [DAY_STATUS.SICK_APPROVED]:     { td: 'bg-danger text-white font-bold' },
  [DAY_STATUS.SICK_PENDING]:      { td: 'bg-danger/15', box: 'rounded border border-dashed border-danger' },
  [DAY_STATUS.ABSENCE_APPROVED]:  { td: 'bg-warning/30' },
  [DAY_STATUS.ABSENCE_PENDING]:   { td: 'bg-warning/10', box: 'rounded border border-dashed border-warning' },
  [DAY_STATUS.COMPENSATION]:      { td: 'bg-violet-200' },
  [DAY_STATUS.HOLIDAY]:           { td: 'bg-emerald-200' },
  [DAY_STATUS.WORKED]:            { td: 'bg-primary/80' },
  [DAY_STATUS.WORKED_PENDING]:    { td: 'bg-primary/20', box: 'rounded border border-dashed border-primary' },
  [DAY_STATUS.REST]:              { td: 'bg-slate-50' },
};

const isVacationStatus = (st) => st === DAY_STATUS.VACATION_APPROVED || st === DAY_STATUS.VACATION_PENDING;

// ─── Patrón canónico de jornada ───────────────────────────────────────────
// Convierte el array de slots de /api/workhours/:id en un objeto indexado
// por dayOfWeek (0=Lun..6=Dom) y produce un string canónico para agrupar.
// El string canónico es estable: dos trabajadores con la misma jornada lo
// comparten exactamente, lo que permite agruparlos por igualdad.
const trim5 = (s) => s ? String(s).slice(0, 5) : '';

const slotPatternForDay = (slot) => {
  if (!slot) return 'D';                   // Descanso (sin row)
  const m = trim5(slot.in1) && trim5(slot.out1) ? `${trim5(slot.in1)}-${trim5(slot.out1)}` : '';
  const t = trim5(slot.in2) && trim5(slot.out2) ? `${trim5(slot.in2)}-${trim5(slot.out2)}` : '';
  if (m && t) return `${m}/${t}`;
  return m || t || 'D';
};

const buildCanonicalKey = (workhoursList) => {
  const byDay = new Map();
  for (const s of (Array.isArray(workhoursList) ? workhoursList : [])) {
    byDay.set(Number(s.dayOfWeek), s);
  }
  return DAYS_OF_WEEK.map(({ value }) => slotPatternForDay(byDay.get(value))).join('|');
};

// Convierte el key canónico en un string legible para humanos, agrupando
// días consecutivos con el mismo patrón (ej. "L-V 09:00-17:00 · S-D libre").
const humanizePattern = (key) => {
  const parts = key.split('|');
  const groups = [];
  let i = 0;
  while (i < parts.length) {
    let j = i;
    while (j + 1 < parts.length && parts[j + 1] === parts[i]) j++;
    groups.push({ from: i, to: j, value: parts[i] });
    i = j + 1;
  }
  const labelDay = (idx) => DAYS_OF_WEEK[idx].short;
  const human = (val) => {
    if (val === 'D') return 'descanso';
    if (val === 'L') return 'libre';
    return val;
  };
  return groups
    .map((g) => {
      const range = g.from === g.to ? labelDay(g.from) : `${labelDay(g.from)}-${labelDay(g.to)}`;
      return `${range} ${human(g.value)}`;
    })
    .join(' · ');
};

const CompararCalendariosPage = () => {
  const { user } = useAuth();
  // Mes inicial = mes de "hoy" (zona del admin según su ficha; sin datos,
  // Madrid). Cada fila se pinta luego en la zona de SU trabajador.
  const todayKey = todayInZone(timeZoneForWorker(user));
  const [viewYear, setViewYear] = useState(() => Number(todayKey.slice(0, 4)));
  const [viewMonth, setViewMonth] = useState(() => Number(todayKey.slice(5, 7)) - 1);
  const [workers, setWorkers] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [eventsByWorker, setEventsByWorker] = useState({});
  const [workhoursByWorker, setWorkhoursByWorker] = useState({});
  // Histórico de horarios (enriquecido) por trabajador: el descanso de cada
  // día se decide con el horario vigente EN ESA FECHA, igual que en el
  // calendario personal (web y app).
  const [scheduleHistoryByWorker, setScheduleHistoryByWorker] = useState({});
  // Bolsa por trabajador: total automático + manual (igual que la ficha y la
  // app). Cola compartida con Equipo (máx. 3 a la vez, caché de sesión).
  const { balances, request: requestBalance } = useLazyHourBalances();
  const [loading, setLoading] = useState(true);
  const [fetchingEvents, setFetchingEvents] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  // Estado del modal de edición: { period, worker } o null.
  const [editing, setEditing] = useState(null);

  const isMaster = user?.type === 5;
  // Edición/eliminación de vacaciones desde "Comparar": SOLO admin empresa
  // (1) y admin sede (2). El super (5) queda excluido a propósito — no nos
  // toca tocar vacaciones de empresas que no son nuestras y, además, el
  // backend rechaza sendMessage del super porque su companyId es null y el
  // chequeo "sender y receiver en la misma empresa" falla.
  const canEditVacations = user && (user.type === 1 || user.type === 2);

  // Empareja eventos VAC_START/END de un worker y devuelve el periodo que
  // contiene una fecha ISO concreta (o null si no cae en ninguno). Igual
  // lógica que CalendarioPage para mantener consistencia.
  // `tz` = zona del trabajador de la fila (el día de cada evento se decide ahí).
  const findVacationPeriodForDay = useCallback((workerEvents, iso, tz) => {
    const sorted = (workerEvents || [])
      .filter((e) => e.eventType === EVENT_TYPES.VACATION_START || e.eventType === EVENT_TYPES.VACATION_END)
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    let pendingStart = null;
    for (const e of sorted) {
      if (e.eventType === EVENT_TYPES.VACATION_START) {
        pendingStart = e;
      } else if (e.eventType === EVENT_TYPES.VACATION_END && pendingStart) {
        const sIso = dayKeyInZone(pendingStart.timestamp, tz);
        const eIso = dayKeyInZone(e.timestamp, tz);
        if (iso >= sIso && iso <= eIso) {
          return {
            start: pendingStart.timestamp, end: e.timestamp,
            startEvent: pendingStart, endEvent: e,
            approvalStatus: pendingStart.approvalStatus,
          };
        }
        pendingStart = null;
      }
    }
    return null;
  }, []);

  const handleCellClick = useCallback((worker, iso, status) => {
    if (!canEditVacations) return;
    if (!isVacationStatus(status)) return;
    const period = findVacationPeriodForDay(eventsByWorker[worker.id], iso, timeZoneForWorker(worker));
    if (!period) return;
    setEditing({ period, worker });
  }, [canEditVacations, eventsByWorker, findVacationPeriodForDay]);

  const reloadAfterEdit = useCallback(async () => {
    if (!editing?.worker?.id) return;
    try {
      // Año visible ±1 día (el día de Madrid empieza a las 22:00/23:00 UTC).
      const list = await getEventsByRange(
        editing.worker.id,
        addDaysToDayKey(`${viewYear}-01-01`, -1),
        addDaysToDayKey(`${viewYear}-12-31`, 1),
      );
      setEventsByWorker((prev) => ({ ...prev, [editing.worker.id]: Array.isArray(list) ? list : [] }));
    } catch {
      // si falla, el siguiente cambio de mes / refresco recargará todo.
    }
  }, [editing, viewYear]);

  const loadWorkers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = (isMaster || !user?.companyId)
        ? await searchWorkers('')
        : await getCompanyWorkers(user.companyId);
      setWorkers(filterWorkersForUser(Array.isArray(data) ? data : [], user));
    } catch (err) {
      setError(err);
      setWorkers([]);
    } finally {
      setLoading(false);
    }
  }, [user?.companyId, isMaster]);

  useEffect(() => { loadWorkers(); }, [loadWorkers]);

  const loadEventsForSelected = useCallback(async () => {
    if (selectedIds.length === 0) {
      setEventsByWorker({});
      setWorkhoursByWorker({});
      setScheduleHistoryByWorker({});
      return;
    }
    setFetchingEvents(true);
    // Cargamos el AÑO visible completo, igual que el calendario personal
    // (CalendarioPage). Así el emparejado START/END de vacaciones, días
    // completos y ausencias es idéntico en las dos pantallas: antes se
    // cargaba solo mes±1 y un periodo largo (p.ej. una baja de 2 meses) se
    // quedaba sin START y no se pintaba aquí pero sí en el calendario.
    // Cambiar de mes dentro del mismo año ya no vuelve a pedir nada.
    // ±1 día de margen: el 1-ene de Madrid empieza a las 23:00 UTC del 31-dic.
    const startIso = addDaysToDayKey(`${viewYear}-01-01`, -1);
    const endIso = addDaysToDayKey(`${viewYear}-12-31`, 1);
    const evOut = {};
    const whOut = {};
    const histOut = {};
    await Promise.all(selectedIds.map(async (id) => {
      try {
        const evs = await getEventsByRange(id, startIso, endIso);
        evOut[id] = Array.isArray(evs) ? evs : [];
      } catch {
        evOut[id] = [];
      }
      try {
        const wh = await getWorkHours(id);
        whOut[id] = Array.isArray(wh) ? wh : [];
      } catch {
        whOut[id] = [];
      }
      try {
        histOut[id] = await fetchEnrichedScheduleHistory(id);
      } catch {
        histOut[id] = [];
      }
    }));
    setEventsByWorker(evOut);
    setWorkhoursByWorker(whOut);
    setScheduleHistoryByWorker(histOut);
    setFetchingEvents(false);
  }, [selectedIds, viewYear]);

  useEffect(() => { loadEventsForSelected(); }, [loadEventsForSelected]);

  const days = useMemo(() => buildDaysOfMonth(viewYear, viewMonth), [viewYear, viewMonth]);

  const filteredWorkers = useMemo(() => {
    if (!query) return workers;
    const q = query.toLowerCase();
    return workers.filter((w) => `${w.name || ''} ${w.email || ''} ${w.nif || ''}`.toLowerCase().includes(q));
  }, [workers, query]);

  const selectedWorkers = useMemo(
    () => selectedIds.map((id) => workers.find((w) => w.id === id)).filter(Boolean),
    [selectedIds, workers]
  );

  // Agrupa los trabajadores seleccionados por su patrón canónico de jornada.
  // Devuelve un array [{ key, label, workers }] ordenado por número de
  // trabajadores en cada grupo (los grupos más numerosos arriba).
  const groupsByPattern = useMemo(() => {
    if (selectedWorkers.length === 0) return [];
    const map = new Map();
    for (const w of selectedWorkers) {
      const key = buildCanonicalKey(workhoursByWorker[w.id]);
      if (!map.has(key)) map.set(key, { key, label: humanizePattern(key), workers: [] });
      map.get(key).workers.push(w);
    }
    return Array.from(map.values()).sort((a, b) => b.workers.length - a.workers.length);
  }, [selectedWorkers, workhoursByWorker]);

  // Comunidad + provincia + LOCALIDAD: sin la localidad, los festivos locales
  // de otra localidad de la misma provincia se aplicaban a todos. EMPRESA:
  // los festivos de empresa solo aplican a los trabajadores de esa empresa.
  const holidayWorkers = useMemo(
    () => selectedWorkers.map((w) => ({
      id: w.id,
      autonomousCommunity: w.autonomousCommunity,
      community: w.autonomousCommunity,
      province: w.province,
      locality: w.locality,
      companyId: w.companyId ?? null,
    })),
    [selectedWorkers]
  );
  const { byWorker: holidaysByWorker, byWorkerDetail: holidayDetailByWorker } = useMultiHolidays(holidayWorkers, viewYear);

  // Sets de estado por trabajador (mismo helper que el calendario personal).
  // Cada fila en la zona de SU trabajador (Canarias / península): el mismo
  // día sale igual que en su calendario, en cualquier navegador.
  const statusSetsByWorker = useMemo(() => {
    const m = new Map();
    for (const w of selectedWorkers) m.set(w.id, buildDayStatusSets(eventsByWorker[w.id] || [], timeZoneForWorker(w)));
    return m;
  }, [selectedWorkers, eventsByWorker]);

  // Resolver del horario POR FECHA de cada trabajador (histórico enriquecido o,
  // sin histórico, horario actual) para marcar el descanso con la MISMA regla
  // que el calendario personal de web y app (isRestDayAt, src/lib/
  // scheduleHistory.js). Antes, sin jornada cargada se inventaba un fin de
  // semana; el calendario personal marca todos los días como descanso.
  const slotResolverByWorker = useMemo(() => {
    const m = new Map();
    for (const w of selectedWorkers) {
      const wh = Array.isArray(workhoursByWorker[w.id]) ? workhoursByWorker[w.id] : [];
      const enriched = buildEnrichedScheduleHistory(scheduleHistoryByWorker[w.id], wh);
      m.set(w.id, makeScheduleResolver(enriched, wh, timeZoneForWorker(w)));
    }
    return m;
  }, [selectedWorkers, workhoursByWorker, scheduleHistoryByWorker]);

  // La bolsa de los seleccionados se pide siempre (el usuario los eligió).
  useEffect(() => {
    for (const w of selectedWorkers) requestBalance(w);
  }, [selectedWorkers, requestBalance]);

  const toggleWorker = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const selectAll = () => setSelectedIds(filteredWorkers.map((w) => w.id));
  const clearAll = () => setSelectedIds([]);

  const goPrev = () => {
    if (viewMonth === 0) { setViewYear(viewYear - 1); setViewMonth(11); }
    else setViewMonth(viewMonth - 1);
  };
  const goNext = () => {
    if (viewMonth === 11) { setViewYear(viewYear + 1); setViewMonth(0); }
    else setViewMonth(viewMonth + 1);
  };

  return (
    <div className="h-[calc(100vh-64px-4rem)] flex flex-col min-h-0">
      <div className="mb-5">
        <Link to="/panel/equipo" className="inline-flex items-center gap-1 text-sm font-semibold text-ink-muted hover:text-ink transition-colors mb-3">
          <ChevronLeft size={16} /> Volver al equipo
        </Link>
        <div className="flex items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold mb-1">Comparación</div>
            <h1 className="font-display text-2xl md:text-3xl font-bold text-ink">Calendarios en paralelo</h1>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={goPrev} className="p-2 rounded-lg text-ink-muted hover:text-ink hover:bg-slate-100 transition-colors">
              <ChevronLeft size={18} />
            </button>
            <div className="text-center">
              <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold">{viewYear}</div>
              <div className="font-display text-xl font-bold text-ink">{MESES[viewMonth]}</div>
            </div>
            <button onClick={goNext} className="p-2 rounded-lg text-ink-muted hover:text-ink hover:bg-slate-100 transition-colors">
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="border border-danger/30 bg-danger/5 rounded-xl p-3 mb-4 flex items-start gap-3">
          <AlertTriangle size={16} className="text-danger shrink-0 mt-0.5" />
          <div className="flex-1 text-sm font-semibold text-ink">{error.message}</div>
        </div>
      )}

      <div className="flex-1 min-h-0 grid lg:grid-cols-[280px_1fr] gap-5 overflow-hidden">
        <aside className="flex flex-col min-h-0 border border-slate-200 rounded-xl bg-white">
          <div className="p-3 border-b border-slate-200">
            <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold mb-2 inline-flex items-center gap-2">
              <Users size={12} /> Trabajadores
              <span className="text-ink font-bold">{selectedIds.length}/{workers.length}</span>
            </div>
            <div className="relative mb-2">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar..."
                className="pl-8 pr-2 py-1.5 border border-slate-200 rounded-md focus:outline-none focus:border-primary text-xs w-full"
              />
            </div>
            <div className="flex items-center gap-2 text-xs">
              <button onClick={selectAll} className="text-primary font-semibold hover:underline">Todos</button>
              <span className="text-ink-muted">·</span>
              <button onClick={clearAll} className="text-ink-muted font-semibold hover:text-ink">Ninguno</button>
            </div>
          </div>
          <ul className="flex-1 overflow-y-auto divide-y divide-slate-100">
            {loading ? (
              <li className="py-10 text-center text-sm text-ink-muted">Cargando…</li>
            ) : filteredWorkers.length === 0 ? (
              <li className="py-10 text-center text-xs text-ink-muted">Sin resultados.</li>
            ) : (
              filteredWorkers.map((w) => {
                const selected = selectedIds.includes(w.id);
                return (
                  <li key={w.id}>
                    <button
                      onClick={() => toggleWorker(w.id)}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm transition-colors ${
                        selected ? 'bg-primary-soft' : 'hover:bg-slate-50'
                      }`}
                    >
                      <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                        selected ? 'bg-primary border-primary' : 'border-slate-300 bg-white'
                      }`}>
                        {selected && <Check size={12} className="text-white" />}
                      </span>
                      <span className="flex-1 min-w-0">
                        <div className="font-semibold text-ink truncate">{w.name}</div>
                        <div className="text-xs text-ink-muted truncate">{w.email}</div>
                      </span>
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </aside>

        <section className="flex flex-col min-h-0">
          <div className="flex items-center gap-3 mb-3 text-xs flex-wrap">
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-primary/80" /> Trabajado</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-accent" /> Vacaciones</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-danger" /> Día completo</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-warning/30" /> Por horas</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-violet-200" /> Compensación</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-emerald-200" /> Festivo</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-white border border-dashed border-ink-muted" /> Pendiente</span>
            {fetchingEvents && <span className="ml-auto text-ink-muted inline-flex items-center gap-1"><Loader2 size={11} className="animate-spin" /> Cargando eventos…</span>}
          </div>

          {selectedIds.length === 0 ? (
            <div className="flex-1 flex items-center justify-center border border-dashed border-slate-300 rounded-xl text-ink-muted text-sm">
              Selecciona uno o más trabajadores en el panel de la izquierda para comparar sus calendarios.
            </div>
          ) : (
            <div className="flex-1 min-h-0 overflow-auto border border-slate-200 rounded-xl bg-white">
              <table className="w-full text-xs border-collapse">
                <thead className="sticky top-0 bg-background z-10">
                  <tr>
                    <th className="text-left font-semibold py-2 px-3 border-b border-slate-200 sticky left-0 bg-background z-20 min-w-[180px]">
                      Trabajador
                    </th>
                    {days.map((d) => {
                      const dowLabel = DOW[d.dow];
                      const isWeekend = d.dow >= 5;
                      return (
                        <th
                          key={d.iso}
                          className={`text-center font-semibold py-2 px-1 border-b border-slate-200 min-w-[32px] ${
                            isWeekend ? 'bg-slate-50' : ''
                          }`}
                        >
                          <div className="text-[9px] text-ink-muted font-normal">{dowLabel}</div>
                          <div className="text-ink tabular-nums">{d.day}</div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {groupsByPattern.map((group) => (
                    <React.Fragment key={group.key}>
                      <tr className="bg-primary-soft/60">
                        <td
                          colSpan={days.length + 1}
                          className="sticky left-0 z-10 bg-primary-soft/60 px-3 py-1.5 border-y border-primary-light"
                        >
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[10px] uppercase tracking-wider font-bold text-primary-darker">Grupo</span>
                            <span className="text-xs font-semibold text-ink">{group.label || 'Sin jornada configurada'}</span>
                            <span className="text-[10px] text-ink-muted">
                              {group.workers.length} trabajador{group.workers.length === 1 ? '' : 'es'}
                            </span>
                          </div>
                        </td>
                      </tr>
                      {group.workers.map((w) => {
                        const holidaysSet = holidaysByWorker.get(w.id) || null;
                        const holidayDetail = holidayDetailByWorker.get(w.id) || null;
                        const statusSets = statusSetsByWorker.get(w.id);
                        const resolveSlot = slotResolverByWorker.get(w.id);
                        const getStatus = (d) => resolveDayStatus(d.iso, statusSets, {
                          holidaysSet,
                          isRestDay: isRestDayAt(resolveSlot, d.iso),
                        });
                        return (
                          <tr key={w.id} className="border-b border-slate-100">
                            <td className="py-1.5 px-3 sticky left-0 bg-white z-10 min-w-[200px] border-r border-slate-200">
                              <Link to={`/panel/equipo/${w.id}`} className="block hover:text-primary transition-colors">
                                <div className="font-semibold text-ink truncate">{w.name}</div>
                                <div className="text-[10px] text-ink-muted truncate">
                                  {w.province ? `${w.province}${w.locality ? ` · ${w.locality}` : ''}` : w.email}
                                </div>
                              </Link>
                              {(() => {
                                if (!eligibleForBalance(w)) return null;
                                const entry = balances.get(Number(w.id));
                                if (!entry || entry.status !== 'done') return null;
                                // Sin horario asignado: no hay número.
                                if (entry.noSchedule) {
                                  return (
                                    <span
                                      className="inline-block mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold italic bg-slate-100 text-ink-muted"
                                      title={`${formatNoScheduleBalance(entry.manual)}. ${NO_SCHEDULE_ADMIN_HINT}.`}
                                    >
                                      {NO_SCHEDULE_SHORT_LABEL}
                                    </span>
                                  );
                                }
                                const mins = entry.total;
                                if (mins === 0) return null;
                                const color = balanceColor(mins);
                                const cls = color === 'positive' ? 'bg-success/10 text-success'
                                          : color === 'negative' ? 'bg-danger/10 text-danger'
                                          : 'bg-slate-100 text-ink-muted';
                                return (
                                  <span
                                    className={`inline-block mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold tabular-nums ${cls}`}
                                    title={`Bolsa total: automático ${formatBalance(entry.auto)} + ajustes manuales ${formatBalance(entry.manual)}`}
                                  >
                                    {formatBalance(mins)}
                                  </span>
                                );
                              })()}
                            </td>
                            {days.map((d) => {
                              const st = getStatus(d);
                              const style = st ? CELL_STYLE[st] : null;
                              const clickable = canEditVacations && isVacationStatus(st);
                              const label = st ? DAY_STATUS_LABELS[st] : null;
                              // Nombre(s) del festivo; los de empresa con la
                              // etiqueta "(Festivo de empresa)".
                              const dayHolidays = holidayDetail?.get(d.iso);
                              const holidayText = dayHolidays?.length ? ` · ${describeHolidays(dayHolidays)}` : '';
                              return (
                                <td
                                  key={d.iso}
                                  title={
                                    clickable
                                      ? `${w.name} · ${d.iso} · ${label.toLowerCase()}${holidayText} (click para editar o eliminar)`
                                      : `${w.name} · ${d.iso}${label ? ' · ' + label : ''}${holidayText}`
                                  }
                                  onClick={clickable ? () => handleCellClick(w, d.iso, st) : undefined}
                                  className={`py-1.5 px-0 text-center border border-slate-100 ${
                                    style?.td || 'bg-white'
                                  } ${clickable ? 'cursor-pointer hover:ring-2 hover:ring-primary' : ''}`}
                                >
                                  <div className={`w-5 h-5 mx-auto ${style?.box || ''}`} />
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      <VacationEditModal
        open={!!editing}
        period={editing?.period}
        adminMode
        adminUser={user}
        targetWorker={editing?.worker}
        onClose={() => setEditing(null)}
        onSubmitted={async () => { await reloadAfterEdit(); setEditing(null); }}
      />
    </div>
  );
};

export default CompararCalendariosPage;
